import React from 'react';
import { Alert, Box, Collapse, Button } from '@mui/material';
import { DashboardProvider } from '../../contexts/DashboardContext';
import AppHeader from '../Dashboard/AppHeader';
import AppThemeProvider, { getColorsFromConfig } from '../Dashboard/AppThemeProvider';
import GlobalFiltersBar from '../Dashboard/GlobalFiltersBar';
import DashboardChatBar from '../Dashboard/DashboardChatBar';
import DashboardLayout from '../Dashboard/DashboardLayout';
import { v2AppToDashboardConfig } from '../../utils/studioV2AppDashboardAdapter';

function gradientBackground(config) {
  const theme = config?.theme || {};
  if ((theme.headerStyle || 'flat') !== 'gradient') return undefined;
  const colors = getColorsFromConfig(theme);
  const hex = String(colors.primary || '#2563eb').replace(/^#/, '');
  if (hex.length !== 6) return undefined;
  const rgb = [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)]
    .map((part) => parseInt(part, 16))
    .join(',');
  return `linear-gradient(180deg, rgba(${rgb}, 0.06) 0%, transparent 18%)`;
}

function CitizenOptionalChat({ darkMode }) {
  const [open, setOpen] = React.useState(false);
  const [voiceBoot, setVoiceBoot] = React.useState(false);
  const openChat = (withVoice = false) => {
    setVoiceBoot(Boolean(withVoice));
    setOpen(true);
  };
  return (
    <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ px: 2, py: 0.75, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          onClick={() => {
            if (open) {
              setOpen(false);
              setVoiceBoot(false);
            } else {
              openChat(false);
            }
          }}
          sx={{ textTransform: 'none', color: 'var(--citizen-primary, #1565c0)' }}
        >
          {open && !voiceBoot ? 'Hide question box' : 'Ask a question'}
        </Button>
        <Button
          size="small"
          variant={open && voiceBoot ? 'contained' : 'outlined'}
          onClick={() => {
            if (open && voiceBoot) {
              setOpen(false);
              setVoiceBoot(false);
            } else {
              openChat(true);
            }
          }}
          sx={{
            textTransform: 'none',
            borderColor: 'var(--citizen-primary, #1565c0)',
            color: open && voiceBoot ? '#fff' : 'var(--citizen-primary, #1565c0)',
            bgcolor: open && voiceBoot ? 'var(--citizen-primary, #1565c0)' : 'transparent',
            '&:hover': {
              borderColor: 'var(--citizen-primary, #1565c0)',
              bgcolor: open && voiceBoot
                ? 'color-mix(in srgb, var(--citizen-primary, #1565c0) 88%, #000)'
                : 'color-mix(in srgb, var(--citizen-primary, #1565c0) 10%, #fff)',
            },
          }}
          data-testid="demo-voice-talk-button"
        >
          {open && voiceBoot ? 'End voice chat' : 'Talk with voice'}
        </Button>
      </Box>
      <Collapse in={open}>
        <DashboardChatBar
          key={voiceBoot ? 'voice' : 'type'}
          darkMode={darkMode}
          startInConversation={voiceBoot}
        />
      </Collapse>
    </Box>
  );
}

export default function StudioV2AppRuntimeShell({
  definition,
  mongoId,
  darkMode = false,
  studioPreview = false,
  showPreviewNotice = false,
  demoReadOnly = false,
  citizenMode = false,
  hideAppHeader = false,
  height,
  pluginPanels,
  chrome = null,
  aside = null,
  footer = null,
}) {
  const config = v2AppToDashboardConfig(definition, {
    mongoId,
    studioPreview,
    demoReadOnly,
    citizenMode,
    pluginPanels,
  });
  const isCitizen = citizenMode || config.citizenMode === true;
  const gradientBg = !isCitizen ? gradientBackground(config) : undefined;
  // Prefer filling the flex parent (demo/portal chrome) over guessing chrome height.
  const shellHeight = height ?? (studioPreview ? '100%' : '100vh');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: shellHeight,
        flex: studioPreview && height == null ? 1 : undefined,
        minHeight: 0,
        overflow: 'hidden',
        borderRadius: studioPreview && !isCitizen ? 2 : 0,
        ...(gradientBg && { background: gradientBg }),
      }}
    >
      <AppThemeProvider key={config._id || config.id} config={config} darkMode={darkMode}>
        <DashboardProvider config={config}>
          {showPreviewNotice && !isCitizen && (
            <Alert severity="info" sx={{ borderRadius: 0 }}>
              Live preview runs saved flows referenced by this app. Unsaved flow edits from the workspace are not executed here.
            </Alert>
          )}
          {chrome}
          {!hideAppHeader && !isCitizen && <AppHeader config={config} />}
          {!hideAppHeader && isCitizen && !chrome && <AppHeader config={config} />}
          {!isCitizen && config.chatPosition === 'header' && <DashboardChatBar darkMode={darkMode} />}
          {!isCitizen && <GlobalFiltersBar />}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              // Aside is desktop rail only for citizen (SFF uses a properties drawer).
              flexDirection: { xs: 'column', md: aside ? 'row' : 'column' },
              px: isCitizen ? { md: 1.25 } : 0,
              pb: isCitizen ? { md: 1.25 } : 0,
              pt: isCitizen ? { md: 1 } : 0,
            }}
          >
            <Box sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: isCitizen ? 'auto' : 'hidden',
              display: 'flex',
              flexDirection: 'column',
              WebkitOverflowScrolling: isCitizen ? 'touch' : undefined,
              ...(isCitizen ? {
                bgcolor: '#fff',
                borderRadius: { md: 1.5 },
                border: { md: 1 },
                borderColor: { md: 'divider' },
                boxShadow: { md: '0 1px 2px rgba(15,23,42,0.04)' },
              } : {}),
            }}
            >
              <DashboardLayout config={config} darkMode={darkMode} />
            </Box>
            {aside && (
              <Box
                sx={{
                  display: { xs: isCitizen ? 'none' : 'block', md: 'flex' },
                  flexDirection: 'column',
                  flex: { md: '0 0 300px', lg: '0 0 340px' },
                  width: { md: 300, lg: 340 },
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'hidden',
                  alignSelf: 'stretch',
                  ...(isCitizen ? {
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    ml: 1.25,
                  } : {}),
                }}
              >
                {aside}
              </Box>
            )}
          </Box>
          {/* Citizen public demos use form → advise API; hide Studio chat dead-end. */}
          {isCitizen && !studioPreview && <CitizenOptionalChat darkMode={darkMode} />}
          {footer}
        </DashboardProvider>
      </AppThemeProvider>
    </Box>
  );
}
